import { Box, Typography, useTheme, Button, MenuItem, TextField, Menu } from "@mui/material";
import { useNavigate }  from "react-router-dom";
import { useSelector }  from "react-redux";
import { useState }     from "react";

import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import FilterListIcon   from "@mui/icons-material/FilterList";
import DownloadIcon     from "@mui/icons-material/Download";
import FormatListIcon   from "@mui/icons-material/FormatListBulleted";

import ReportTable, { RRow, RCell } from "components/Tables/ReportTable";
import { useNearExpiryStockList } from "api/hooks/inventory";
import { PDFDownloadLink } from "@react-pdf/renderer";
import NearExpiryPdf       from "scenes/reports/pdfForms/inventory/nearExpiredStockPdf";

const LIMIT_TYPES = ["TODAY", "DAYS", "WEEKS"];

export default function NearExpiryStock() {
  const navigate = useNavigate();
  const theme    = useTheme();
  const isDark   = theme.palette.mode === "dark";

  const bodyBg  = theme.palette.background.alt;
  const cardBg  = isDark ? "rgba(255,255,255,0.03)" : "#ffffff";
  const accent  = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;
  const textSec = theme.palette.neutral.main;
  const border  = isDark ? "rgba(29,158,117,0.14)" : "rgba(29,158,117,0.16)";

  const business_branch_id = useSelector((s) => s.authReducer.business_branch_id);
  const user               = useSelector((s) => s.authReducer.user);
  const { nearExpiryStockList, isLoadingNearExpiryStockList, setNewPeriod, setExpiryStartDate } = useNearExpiryStockList(1, business_branch_id);

  const [periodType,   setPeriodType]   = useState("TODAY");
  const [periodInput,  setPeriodInput]  = useState(1);
  const [exportAnchor, setExportAnchor] = useState(null);

  const exportOpen = Boolean(exportAnchor);
  const inputSx = {
    "& .MuiInputBase-root": { fontSize: "13px", height: "36px", backgroundColor: cardBg, borderRadius: "8px" },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: border },
    "& .MuiInputLabel-root": { fontSize: "12px" },
  };
  const menuItemSx = { fontSize: "13px", color: textPri, borderRadius: "6px", mx: "4px", "&:hover": { backgroundColor: "rgba(29,158,117,0.08)" } };

  const columns = [
    { label: "#",               width: 40  },
    { label: "Item Name"                   },
    { label: "Description"                 },
    { label: "Expiry Limit (Days)", align: "center" },
    { label: "Batch",           align: "center" },
    { label: "Expiry Date",     align: "center" },
    { label: "Remaining Qty",   align: "center" },
    { label: "Days To Expiry",  align: "center" },
  ];

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: bodyBg, p: "1.5rem 2rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Box sx={{ width: "40px", height: "40px", borderRadius: "10px", backgroundColor: "rgba(186,117,23,0.1)", display: "flex", alignItems: "center", justifyContent: "center", color: "#BA7517", flexShrink: 0 }}>
            <WarningAmberIcon sx={{ fontSize: "22px" }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontSize: "22px", fontWeight: 400, color: textPri, lineHeight: 1.2 }}>Near Expiry Items</Typography>
            <Typography sx={{ fontSize: "12px", color: "#BA7517", mt: "2px" }}>{nearExpiryStockList.length} items near expiry</Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: "0.5rem" }}>
          <Button onClick={() => navigate("/productLogList", { state: { productList: [] } })} startIcon={<FormatListIcon sx={{ fontSize: "16px" }} />}
            sx={{ fontSize: "13px", fontWeight: 500, px: "1rem", py: "0.45rem", borderRadius: "8px", border: `1px solid ${border}`, color: textSec, backgroundColor: "transparent", textTransform: "none", "&:hover": { backgroundColor: "rgba(29,158,117,0.06)", borderColor: accent, color: accent } }}>
            View All Logs
          </Button>
          <Button onClick={(e) => setExportAnchor(e.currentTarget)} endIcon={<DownloadIcon sx={{ fontSize: "16px" }} />}
            sx={{ fontSize: "13px", fontWeight: 500, px: "1rem", py: "0.45rem", borderRadius: "8px", border: `1px solid ${border}`, color: accent, backgroundColor: "rgba(29,158,117,0.06)", textTransform: "none", "&:hover": { backgroundColor: "rgba(29,158,117,0.12)" } }}>
            Export
          </Button>
          <Menu anchorEl={exportAnchor} open={exportOpen} onClose={() => setExportAnchor(null)}
            PaperProps={{ sx: { mt: "6px", minWidth: "160px", backgroundColor: cardBg, border: `1px solid ${border}`, borderRadius: "10px", boxShadow: "0 4px 20px rgba(0,0,0,0.1)" } }}>
            <MenuItem onClick={() => setExportAnchor(null)} sx={menuItemSx}>
              <PDFDownloadLink document={<NearExpiryPdf viewData={{ report_detail: nearExpiryStockList }} printedBy={`${user.first_name} ${user.last_name}`} />} fileName="Near Expiry Stock Report.pdf" style={{ textDecoration: "none", color: "inherit" }}>Download PDF</PDFDownloadLink>
            </MenuItem>
            <MenuItem onClick={() => setExportAnchor(null)} sx={menuItemSx}>Download Excel</MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* Filter */}
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "0.75rem", p: "0.75rem 1rem", backgroundColor: cardBg, border: `1px solid ${border}`, borderRadius: "10px" }}>
        <FilterListIcon sx={{ fontSize: "16px", color: accent }} />
        <Typography sx={{ fontSize: "11px", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.07em", color: accent }}>Expiry limit from</Typography>
        <Box sx={{ display: "flex", gap: "5px" }}>
          {LIMIT_TYPES.map((p) => (
            <Box key={p} onClick={() => { setPeriodType(p); setNewPeriod(p); }} sx={{ px: "0.65rem", py: "0.25rem", borderRadius: "20px", fontSize: "12px", fontWeight: 500, cursor: "pointer", border: `1px solid ${periodType === p ? accent : border}`, color: periodType === p ? accent : textSec, backgroundColor: periodType === p ? "rgba(29,158,117,0.1)" : "transparent", transition: "all 0.15s", "&:hover": { borderColor: accent, color: accent } }}>
              {p === "TODAY" ? "Today" : p === "DAYS" ? "Days" : "Weeks"}
            </Box>
          ))}
        </Box>
        {periodType !== "TODAY" && (
          <>
            <TextField label={`Number of ${periodType.toLowerCase()}`} type="number" size="small" defaultValue={1}
              onChange={(e) => setPeriodInput(e.target.value)} inputProps={{ min: 1 }} sx={{ ...inputSx, width: "140px" }} />
            <Button onClick={() => setExpiryStartDate(periodInput)} sx={{ fontSize: "12px", fontWeight: 500, px: "1rem", py: "0.35rem", borderRadius: "8px", backgroundColor: accent, color: "#fff", textTransform: "none", "&:hover": { backgroundColor: "#0F6E56" } }}>Apply</Button>
          </>
        )}
      </Box>

      <ReportTable columns={columns} rows={nearExpiryStockList} loading={isLoadingNearExpiryStockList}
        emptyMessage="No near-expiry items found"
        renderRow={(row, index) => {
          const days = Number(row.days_to_expiry);
          return (
            <RRow key={row.id} index={index}>
              <RCell>{index + 1}</RCell>
              <RCell><Typography sx={{ fontSize: "13px", fontWeight: 500, color: textPri }}>{row.related_product.product_name}</Typography></RCell>
              <RCell>{row.related_product.description}</RCell>
              <RCell align="center">{row.related_product.expiry_limit}</RCell>
              <RCell align="center">{row.batch}</RCell>
              <RCell align="center">{row.expiry_date}</RCell>
              <RCell align="center">{row.remaining_quantity}</RCell>
              <RCell align="center">
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: "5px", px: "8px", py: "3px", borderRadius: "20px", backgroundColor: days < 0 ? "rgba(226,75,74,0.1)" : "rgba(186,117,23,0.1)" }}>
                  <Box sx={{ width: "5px", height: "5px", borderRadius: "50%", backgroundColor: days < 0 ? "#e24b4a" : "#BA7517", flexShrink: 0 }} />
                  <Typography sx={{ fontSize: "12px", fontWeight: 600, color: days < 0 ? "#e24b4a" : "#BA7517" }}>{days}</Typography>
                </Box>
              </RCell>
            </RRow>
          );
        }}
      />
    </Box>
  );
}